'use client';

import { useState, useCallback } from 'react';
import { errorLogging } from 'services/api/client';

type AnyFn<T, E> = (...args: unknown[]) => Promise<T | E> | T | E;

/**
 * Custom hook for safe execution of async functions.
 * Handles error state, loading state, and logs errors.
 */
export function useSafeExecution() {
  const [error, setError] = useState<unknown>(null); // Stores the last error
  const [pending, setPending] = useState(false); // Indicates if execution is in progress

  /**
   * Executes the given callback safely.
   * Catches errors, sets error state, logs errors, and supports an optional error handler.
   * @param callback - The function to execute
   * @param onErrorCaught - Optional callback for caught errors
   * @returns The result of the callback or null if an error occurred
   */
  const execute = useCallback(
    async <T, E>(callback: AnyFn<T, E> | undefined, onErrorCaught?: (error: E) => void): Promise<T | E | null> => {
      if (!callback) return null;
      setPending(true);
      setError(null);

      try {
        const result = await callback();

        return result;
      } catch (err) {
        setError(err);

        if (onErrorCaught) {
          onErrorCaught(err as E);
        }

        if (err instanceof Error) {
          errorLogging(err);
        } else {
          errorLogging(new Error(String(err)));
        }

        return null;
      } finally {
        setPending(false);
      }
    },
    [],
  );

  return {
    execute, // Function to safely execute callbacks
    error, // Last error object
    pending, // Loading status
    clearError: () => setError(null), // Function to clear the error state
  };
}
